import React from "react";

import { Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";

import bg from "../../assets/images/articles/Aucoin/Aucoin-7.jpg";
import img1 from "../../assets/images/articles/Aucoin/Aucoin-1.jpg";
import img2 from "../../assets/images/articles/Aucoin/Aucoin-2.jpg";
import img3 from "../../assets/images/articles/Aucoin/Aucoin-8.jpg";
import img4 from "../../assets/images/articles/Aucoin/Aucoin-5.jpg";
import img5 from "../../assets/images/articles/Aucoin/Aucoin-9.jpg";

import downloadArticle from "../../assets/pdf/Au-coin_in-den-hoek.pdf";

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "white",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 400,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid black",
      borderRadius: "1px",
    },
  })
);

function Aucoin() {
  return (
    <div className="Aucoin" id="background">
      <div className="Aucoin__backgroundVideo">
        <div className="Aucoin__backgroundVideo__wrapper">
          <div className="Aucoin__backgroundVideo__title">
            <h1 className="editH1">Au coin - in den hoek</h1>
            <h2 className="editH2">Anonymous | 18.10.2025</h2>
          </div>
        </div>
        <img src={bg} className="Aucoin_bg_img" alt="" />
      </div>

      <div className="Aucoin__container">
        <div className="Aucoin__container__title">
          <h1 className="editH1">Au coin - in den hoek</h1>
          <h2 className="editH2">Anonymous | 18.10.2025</h2>
        </div>

        <div className="Aucoin__container__intro">
          <div className="Aucoin__intro__img">
            <img src={img1} alt="" />
            <p className="credit__margin">@Simon Breynaert </p>
          </div>

          <div className="Aucoin__intro__text">
            This is an interview between Anonymous and Anonymous 1; and Anonymous 2; and Anonymous 3.
          </div>
        </div>

        <div className="Aucoin__container__body pad__top">
          <div className="Aucoin__body__text">
            <div>
              <h2>Why did we go to two corners?</h2>
              Because while walking across Schaerbeek, I found two very nice-looking corners that were fenced off for no
              clear reason. At that moment, I was feeling restless and eager to finally do some ventures in Brussels. I
              had been missing “fenching” so much, and it felt sad not to have it around. When I saw those corners, I
              realized that was the spot — that was the thing to do.
            </div>
            <div>
              <h2>What is fenching?</h2>
              Fenching is a movement I’m part of. It started in Russia a few years ago, when we began gathering in the
              middle of cities to build small benches on top of fences. <mark>Fences</mark> are a big issue in Russia,
              both literally and symbolically. Very quickly, the project evolved from simply creating objects to
              becoming a way to reclaim public space — to perform our right to the city.
              <br />
              <br />
              We managed to gather groups of 30–50 people in central Moscow or St. Petersburg, which was remarkable
              because public assembly is heavily restricted there. Even a one-person protest isn’t allowed, so this was
              a big step.
            </div>
            <div>
              <h2>And now you do that outside of Russia too?</h2>
              Yes. In Tbilisi we have a large number of participants, and the movement has spread to Serbia, Paris,
              Berlin, and now Brussels. In each city, the practice takes a different form depending on the local
              context. In Europe, the environment isn’t as aggressive as in Russia, where fences were a real obstacle.
              So the project constantly reinvents itself.
              <br />
              <br />
              In Paris, for example, the practice became focused on helping migrants unite and find ways to claim space
              — in cities, abandoned buildings, or suburbs. In Brussels, it’s a bit different. It started more from a
              sense of loneliness and a desire to reconnect with the practice, to find friends and community. Thanks to
              Club Dérive, we managed to connect — that’s actually how we met them. It became a reason to come together.
              People from Paris even joined us, which was great.
              <br />
              <br />
              We’ve also been trying something we call <i>anarcho-paratroopers</i> — a rough translation from Russian.
              It’s similar to temporary laboratories, where a group of people comes to a place for a short time to do
              something <mark>site-specific</mark>. Usually, these labs can be problematic — either they’re too invasive
              and unethical, or too detached to make a difference. So we tried to reinvent this model.
              <br />
              <br />
              When someone in one city feels they have something to do but lacks a group, they can call friends through
              our network. People come from different cities and stay on each other’s couches, creating a temporary but
              connected collective. This way, the “laboratory” feels more genuine — mixing locals who know the space
              with visitors who bring new energy. That’s also what happened in Brussels: people from the Paris Fenching
              group joined, and for them it was a nice introduction to the city.
            </div>
          </div>
          <div className="Aucoin__body__image">
            <img src={img2} alt="" />
          </div>
        </div>

        <div className="CAL__container__body">
          <div className="CAL__body__text__quote">
            <h2>
              “The right to the city, as defined by Lefebvre, is something you <br />
              actively fight for ”
            </h2>
          </div>
        </div>

        <div className="Aucoin__container__body">
          <div className="Aucoin__body__text__middle" id="get__H2">
            <h2>About the right to the city and being invasive</h2>
            Sometimes, being invasive can be positive — especially in non-spaces, areas defined as “not to be
            appropriated.” <mark>Invading those places can actually reclaim them.</mark> The right to the city, as
            defined by Lefebvre, is something you actively fight for — it exists only in the act of reclaiming it. If
            you’re in an abandoned place, you have to ask whether you’re truly reclaiming it or just using a nice word
            for what you’re doing.
            <br />
            <br />
            Still, migrants and newcomers should have the ability to fight for their space in the city. During our
            action, we might have missed some of that energy — it felt more like design than direct action. But the
            process itself was beautiful in terms of collaboration and relationships. On site, it was more about <br />
            <mark>scavenging</mark> materials, playing with shapes, and building something small — a kind of
            installation or hangout spot. We found a trolley, collected materials from nearby trash, and built small
            structures where you could sit, smoke, or place your drinks. It became a playful exploration of how to
            reclaim even a small corner and turn it into a shared space.
            
            <h2>What was happening on Au coin, in the corner?</h2>
            It was hot — really hot. It was a gathering, an action, and also a kind of search. We were assembling,
            reclaiming, and transforming a place that one of us had discovered earlier. He invited others to join, to
            play with materials in search of new purpose.
            <br />
            <br />
            As we collected materials, it all came together like a <mark>puzzle</mark>. That puzzle became a moment of
            reflection — the pieces, the corner itself, became something new. It turned into a place to connect, not
            only with each other but also with people and materials from other locations. We had some beers. It was hot,
            but it felt right.
            <br />
            <br />
            We imagined what it could have been — maybe more defined, more refined, more aesthetic, with fewer
            materials. It would have been interesting to start with everything and then deconstruct at the same time.
            The piece we left behind wasn’t just about that corner; it was also about curation, assimilation, and
            connection. It’s good that we focused on building, but maybe we could have also explored deconstructing —
            defining what we were doing more consciously.
            <br />
            <br />
            Perhaps we could have done a talk inside the corner, with the speaker trapped among the materials — to give
            voice not only to the people but also to the materials themselves. Maybe they also had something to say.
          </div>
        </div>

        <div className="Aucoin__container__bigImg">
          <img className="Aucoin__big__img" src={img3} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Aucoin__container__body pad__top">
          <div className="Aucoin__body__text">
            <div>
              <h2>Was it more about the final object or about the practice?</h2>
              For me, it’s always an aesthetic adventure. The practice and purpose are there, but fundamentally, it’s
              about energy — group energy, drift energy, the feeling of stumbling upon things unexpectedly. Chance was
              in control of our bodies, and I love that.
              <br />
              <br />I believe it’s important to use what’s in front of you — the materials, the city, the people — and
              at the same time reflect and refine.
              <br />
              <br />
              We even had a moment when someone was “trapped” in the corner and started talking. It sounded ironic —
              full of buzzwords like “reclaiming” and “reusable design.” It was funny, maybe because he placed a
              spaghetti strainer on his head. The contrast between that small, shiny, mass-produced object and the
              rough, improvised structure created an interesting tension.
            </div>
            <div>
              <h2>
                Was there a conflict between the static result and the drifting process? Should it have stayed static or
                not?
              </h2>
              That’s a great question. A drift doesn’t need to end in something static. When people say “this is ugly”
              or “this didn’t work,” they’re expressing emotions — so it did work in a way.
              <br />
              <br />
              We don’t need to create an aesthetically pleasing object from a drift, because the drift itself is an
              aesthetic experience. It’s about opening your senses, deconstructing what materials and movements can
              mean. Drifting means letting go of conventions. Aesthetics is perception — like happiness or sadness —
              it’s the lens through which you see.
              <br />
              <br />A place can be beautiful or ugly depending on how you look at it.
            </div>
            <div>
              <h2>But when a drift produces an aesthetic object, doesn’t that risk turning it into a commodity?</h2>
              Artistically, yes. Drifting and situationism came from movements <mark>against commodification</mark>. But
              for me, drifting is also about freedom and enlightenment. It’s an aesthetic adventure.
              <br />
              <br />
              My perception determines whether I find things beautiful, ugly, or unsettling. Personally, I also need a
              sense of closure — a kind of ritual ending that feels complete. Maybe that’s part of my nature, but it’s
              how I find peace.
              <br />
              <br />
              Drifting and beauty can coexist. I drift for the beauty, even if it doesn’t always result in beauty. The
              process itself — moving through realities, discovering new perspectives — that’s already meaningful.
              Still, I like to bring things together in the end, to create a small harmony, a “piece of mind.”
            </div>
          </div>
          <div className="Aucoin__body__image">
            <img src={img4} alt="" />
          </div>
        </div>

        <div className="Aucoin__container__bigImg">
          <img className="Aucoin__big__img" src={img5} alt="" />
          <p className="credit__margin"> @Simon Breynaert </p>
        </div>

        <div className="Aucoin__container__body">
          <div className="Aucoin__body__text__middle" id="get__H2">
            <h2>
              You were also photographing the process. How did that mix with building and freezing moments in time?
            </h2>
            Photography is my product — my way to find and express beauty in the moment, not just in materials. It’s my
            visual language. Framing is also a kind of drift — searching within a moment that already exists. I can’t
            change people or architecture, but I can drift with my eye.
            <br />
            <br />
            Most people experience aesthetics visually. That’s why photography, videography, and archiving matter — they
            let us translate moments from the past into the future, keeping their energy alive. When we look at those
            photos later, the energy returns.
            <br />
            <br />
            For me, that’s my contribution — creating an aesthetically complete ending, a “peace of mind.”
            <h2>Anything you’d like to add?</h2>
            Just that I’m grateful for that moment. Sometimes it’s hard to organize or manage for others, but it’s so
            important to bring people together. I want to end by saying that love is the most important thing in this
            world.
            <br />
            <br />
            We need more of these moments. Fences — those corners made to block — can be transformed into spaces of
            love. Maybe not for peeing, but for being together, for loving the materials, the space, and each other.
            <br />
            <br />
            <mark>It might have looked goofy, but it was layered — political, personal, and full of love. That’s the direction
            we should go.</mark>
          </div>
        </div>

        <div className="CAL__container__body">
          <div className="CAL__body__text__quote">
            <h2>
              “Lost people, found objects.”
            </h2>
          </div>
        </div>

        <div className="Aucoin__container__body">
          <div className="Aucoin__body__text__middle middle__center" id="get__H2">
            <div className="display__flex">
              <div className="article__download__button">
                <a href={downloadArticle} download="article_Au-coin_in-den-hoek">
                  Download Article
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Aucoin;
